/**
 * 应用管理状态 Store
 * 管理用户的应用列表、当前选中的应用等
 */

import { create } from 'zustand';
import type {
  AppInfo,
  CreateAppResult,
  ResetKeyResult,
  AppPushConfigUpdateRequest,
} from '@/api';
import {
  getAppList,
  createApp,
  resetApiKey,
  deleteApp,
} from '@/api';
import { updateAppPushConfig, updateAppStatus } from '@/api/modules/apps.api';
import { AppStatus } from '@/api/modules/apps';

/**
 * 应用管理状态接口
 */
interface AppsState {
  // ==================== 状态 ====================
  
  /** 应用列表 */
  apps: AppInfo[];
  /** 当前选中的应用 ID */
  currentAppId: string | null;
  /** 是否正在加载 */
  isLoading: boolean;
  /** 最后更新时间 */
  lastUpdated: number | null;

  // ==================== Actions ====================
  
  /**
   * 设置应用列表
   * @param apps - 应用列表
   */
  setApps: (apps: AppInfo[]) => void;

  /**
   * 添加应用
   * @param app - 新应用信息
   */
  addApp: (app: AppInfo) => void;

  /**
   * 更新应用
   * @param appId - 应用 ID
   * @param updates - 更新的字段
   */
  updateApp: (appId: string, updates: Partial<AppInfo>) => void;

  /**
   * 删除应用
   * @param appId - 应用 ID
   */
  removeApp: (appId: string) => void;

  /**
   * 设置当前选中的应用
   * @param appId - 应用 ID
   */
  setCurrentApp: (appId: string | null) => void;

  /**
   * 获取当前选中的应用
   * @returns 当前应用信息或 null
   */
  getCurrentApp: () => AppInfo | null;

  /**
   * 设置加载状态
   * @param loading - 是否加载中
   */
  setLoading: (loading: boolean) => void;

  /**
   * 清空应用列表
   */
  clearApps: () => void;

  /**
   * 刷新应用列表（从 API 获取）
   */
  refreshApps: () => Promise<void>;

  /**
   * 创建新应用
   * @param name - 应用名称
   * @returns 创建结果，包含完整的 API Key
   */
  createNewApp: (name: string) => Promise<CreateAppResult | null>;

  /**
   * 重置应用 API Key
   * @param appId - 应用 ID
   * @param signature - 钱包签名
   * @returns 重置结果，包含新的 API Key
   */
  resetAppKey: (
    appId: string,
    signature: string
  ) => Promise<ResetKeyResult | null>;

  /**
   * 删除应用
   * @param appId - 应用 ID
   * @param signature - 钱包签名
   * @returns 是否删除成功
   */
  removeAppWithSignature: (
    appId: string,
    signature: string
  ) => Promise<boolean>;

  /**
   * 切换应用状态（启用/禁用）
   *
   * 注：后端 `PATCH /apps/{id}/status` 现在强制要求钱包签名，
   * 调用方需先通过 `/apps/challenge` + `personal_sign` 获得签名再传入。
   *
   * @param appId - 应用 ID
   * @param currentStatus - 当前状态（0 禁用 / 1 启用）
   * @param signature - 钱包签名（来自 /apps/challenge 的 message + personal_sign）
   * @returns 是否操作成功
   */
  toggleAppStatus: (
    appId: string,
    currentStatus: number,
    signature: string
  ) => Promise<boolean>;

  /**
   * 更新应用 WebSocket / Webhook 推送配置
   *
   * 调用方需先通过 `/apps/challenge` + `personal_sign` 获得签名。
   *
   * @param appId - 应用 ID
   * @param data - 推送配置更新请求体
   * @returns 是否操作成功
   */
  updatePushConfig: (
    appId: string,
    data: AppPushConfigUpdateRequest
  ) => Promise<boolean>;
}

/**
 * 初始状态
 */
const initialState = {
  apps: [],
  currentAppId: null,
  isLoading: false,
  lastUpdated: null,
};

/**
 * 应用管理 Store
 * 
 * @example
 * ```ts
 * import { useAppsStore } from '@/stores';
 * 
 * function AppList() {
 *   const { apps, currentAppId, setCurrentApp } = useAppsStore();
 *   
 *   return (
 *     <div>
 *       {apps.map(app => (
 *         <div key={app.id} onClick={() => setCurrentApp(app.id)}>
 *           {app.name}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useAppsStore = create<AppsState>()(
  (set, get) => ({
      ...initialState,

      setApps: (apps) => {
        set({
          apps,
          lastUpdated: Date.now(),
        });
      },

      addApp: (app) => {
        set((state) => ({
          apps: [...state.apps, app],
          lastUpdated: Date.now(),
        }));
      },

      updateApp: (appId, updates) => {
        set((state) => ({
          apps: state.apps.map((app) =>
            app.id === appId ? { ...app, ...updates } : app
          ),
          lastUpdated: Date.now(),
        }));
      },

      removeApp: (appId) => {
        set((state) => ({
          apps: state.apps.filter((app) => app.id !== appId),
          currentAppId: state.currentAppId === appId ? null : state.currentAppId,
          lastUpdated: Date.now(),
        }));
      },

      setCurrentApp: (appId) => {
        set({ currentAppId: appId });
      },

      getCurrentApp: () => {
        const state = get();
        return state.apps.find((app) => app.id === state.currentAppId) || null;
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      clearApps: () => {
        set(initialState);
      },

      refreshApps: async () => {
        set({ isLoading: true });
        try {
          const result = await getAppList();
          if (result.ok) {
            set({
              apps: result.data.apps,
              lastUpdated: Date.now(),
            });
          }
        } catch (error) {
          console.error('[Apps Store] Failed to refresh apps:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      createNewApp: async (name) => {
        try {
          const result = await createApp({ name });
          if (result.ok) {
            // 添加到列表
            set((state) => ({
              apps: [
                {
                  id: result.data.id,
                  name: result.data.name,
                  keyPrefix: result.data.keyPrefix,
                  status: 1, // 新创建的应用默认为启用状态
                  createdAt: result.data.createdAt,
                  updatedAt: result.data.createdAt,
                  lastUsedAt: null,
                  enable_ws: false,
                  enable_webhook: false,
                  webhook_url: null,
                  rpcService: {
                    status: "disabled",
                    enabled: false,
                    networkCount: 0,
                  },
                  energyRentalService: {
                    status: "disabled",
                    enabled: false,
                    enabledAt: null,
                  },
                },
                ...state.apps,
              ],
              lastUpdated: Date.now(),
            }));
            return result.data;
          }
          return null;
        } catch (error) {
          console.error('[Apps Store] Failed to create app:', error);
          return null;
        }
      },

      resetAppKey: async (appId, signature) => {
        try {
          const result = await resetApiKey(appId, { signature });
          if (result.ok) {
            // 更新应用的 keyPrefix 和 updatedAt
            set((state) => ({
              apps: state.apps.map((app) =>
                app.id === appId
                  ? {
                      ...app,
                      keyPrefix: result.data.keyPrefix,
                      updatedAt: result.data.updatedAt,
                    }
                  : app
              ),
              lastUpdated: Date.now(),
            }));
            return result.data;
          }
          return null;
        } catch (error) {
          console.error('[Apps Store] Failed to reset API key:', error);
          return null;
        }
      },

      removeAppWithSignature: async (appId, signature) => {
        try {
          const result = await deleteApp(appId, { signature });
          if (result.ok) {
            // 从列表中移除
            set((state) => ({
              apps: state.apps.filter((app) => app.id !== appId),
              currentAppId:
                state.currentAppId === appId ? null : state.currentAppId,
              lastUpdated: Date.now(),
            }));
            return true;
          }
          return false;
        } catch (error) {
          console.error('[Apps Store] Failed to delete app:', error);
          return false;
        }
      },

      toggleAppStatus: async (appId, currentStatus, signature) => {
        try {
          // 计算目标状态（取反）
          const targetStatus = currentStatus === AppStatus.ENABLED
            ? AppStatus.DISABLED
            : AppStatus.ENABLED;

          // 调用 API（等待响应）——yaml 现要求 signature 一并上送
          const result = await updateAppStatus(appId, {
            status: targetStatus,
            signature,
          });

          if (result.ok) {
            // API 成功后再更新 UI
            set((state) => ({
              apps: state.apps.map((app) =>
                app.id === appId
                  ? {
                      ...app,
                      status: targetStatus,
                      enable_ws:
                        targetStatus === AppStatus.ENABLED ? app.enable_ws : false,
                      enable_webhook:
                        targetStatus === AppStatus.ENABLED ? app.enable_webhook : false,
                      webhook_url:
                        targetStatus === AppStatus.ENABLED ? app.webhook_url : null,
                      updatedAt: Math.floor(Date.now() / 1000),
                    }
                  : app
              ),
              lastUpdated: Date.now(),
            }));
            return true;
          } else {
            console.error('[Apps Store] Toggle status failed:', result.error);
            return false;
          }
        } catch (error) {
          console.error('[Apps Store] Failed to toggle app status:', error);
          return false;
        }
      },

      updatePushConfig: async (appId, data) => {
        try {
          const result = await updateAppPushConfig(appId, data);
          if (result.ok) {
            set((state) => ({
              apps: state.apps.map((app) =>
                app.id === appId
                  ? {
                      ...app,
                      enable_ws: data.enable_ws ?? app.enable_ws,
                      enable_webhook:
                        data.enable_webhook ?? app.enable_webhook,
                      webhook_url:
                        data.webhook_url !== undefined
                          ? data.webhook_url
                          : app.webhook_url,
                      updatedAt: Math.floor(Date.now() / 1000),
                    }
                  : app
              ),
              lastUpdated: Date.now(),
            }));
            return true;
          }
          console.error('[Apps Store] Update push config failed:', result.error);
          return false;
        } catch (error) {
          console.error('[Apps Store] Failed to update push config:', error);
          return false;
        }
      },
  })
);

/**
 * 便捷 Hooks
 */

/**
 * 获取应用列表
 */
export const useApps = () => useAppsStore((state) => state.apps);

/**
 * 获取当前选中的应用
 */
export const useCurrentApp = () => {
  return useAppsStore((state) => 
    state.apps.find((app) => app.id === state.currentAppId) || null
  );
};

/**
 * 获取加载状态
 */
export const useAppsLoading = () => useAppsStore((state) => state.isLoading);
